import {
  Box,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useOutsideClick,
} from '@chakra-ui/react';
import { Collapse } from 'react-collapse';
import { useDispatch, useSelector } from 'react-redux';
import React, { useRef, useState, useCallback } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import debounce from 'lodash.debounce';

import InputField from 'components/InputField';
import Spinner from 'components/Spinner';
import {
  PostDeleteDimensionItem,
  UpdateItemName,
  fetchDimensionItemsGroup,
  getDataIndicators,
  getDisplayInfo,
  getOpenItem,
  getRenameItemName,
  setDisplayInfo,
  setIsItemRenamed,
  setRenameItemName,
  setopenItem,
  setSelectedElement,
} from 'redux/ItemPageSlice';
import { FetchBlockItemDimensions, getSelectedDriver, handleFilters } from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import DeleteModal from 'components/Modals/DeleteModal';
import { AppDispatch } from 'utils/GlobalHelpers';

import useClickAndDoubleClickHandler from '../../../../hooks/useClickAndDoubleClickHandler';
import arrowDown from '../Icons/arrowDown.svg';
import arrowUp from '../Icons/arrowUp.svg';
import infoIcon from '../Icons/infoIcon.svg';

import DatePickerDrivers from './DatePickerDrivers';
import DesktopProperties from './DesktopProperties';

interface DesktopElementsProperties {
  _index: number;
  dataKey: any;
  styles: any;
  onSubmitValue: (value: string, id: number, propertyValue: string) => void;
  longClickCheck: any;
  isLongClicked: boolean;
  planPermission?: boolean;
}

const DesktopElements = ({
  _index,
  dataKey,
  styles,
  onSubmitValue,
  longClickCheck,
  isLongClicked,
  planPermission,
}: DesktopElementsProperties) => {
  const { blockId } = useParams();
  const [itemNameField, setItemNameField] = useState('');
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [DeleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const [isRenaming, setIsRenaming] = useState<Record<number, boolean>>({});
  const [renameSuccess, setRenameSuccess] = useState<Record<number, boolean>>({});
  const dispatch: AppDispatch = useDispatch();
  const dataIndicators = useSelector(getDataIndicators);
  const displayInfo = useSelector(getDisplayInfo);
  const currentDriver = useSelector(getSelectedDriver);
  const renameItemName = useSelector(getRenameItemName);
  const show = useSelector(getOpenItem);
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const baseScenario = useSelector(getBaseScenario);
  const debounceTimeoutReference = useRef<NodeJS.Timeout | null>(null);
  const isApiCallInProgressReference = useRef<Record<number, boolean>>({});

  const handleShow = (id: number) => {
    if (show === id) {
      return dispatch(setopenItem(null));
    }
    return dispatch(setopenItem(id));
  };

  const renameItem = (event: any, id: number) => {
    event.stopPropagation();
    dispatch(setDisplayInfo(null));
    if (renameItemName === id) {
      return dispatch(setRenameItemName(0));
    }
    return dispatch(setRenameItemName(id));
  };

  useOutsideClick({
    ref: reference,
    handler: () => {
      dispatch(setDisplayInfo(null));
    },
  });

  const toggleInfoPopup = (id: number | null) => {
    if (displayInfo === id) {
      return dispatch(setDisplayInfo(null));
    }
    return dispatch(setDisplayInfo(id));
  };

  const showOrRename = useClickAndDoubleClickHandler(handleShow, renameItem);

  const UpdateItemApi = useCallback(
    async (item_id: number, name: string) => {
      // Prevent multiple simultaneous calls for the same item
      if (isApiCallInProgressReference.current[item_id]) {
        return;
      }

      // Check if name is empty or unchanged
      if (!name || !name.trim()) {
        return;
      }

      // Set loading state
      setIsRenaming((previous) => ({ ...previous, [item_id]: true }));
      setRenameSuccess((previous) => ({ ...previous, [item_id]: false }));
      isApiCallInProgressReference.current[item_id] = true;

      try {
        const requestBody = { name: name.trim(), scenario_id: baseScenario?.id };
        await dispatch(
          UpdateItemName({
            item_id,
            request_body: requestBody,
            toaster: {
              successMessage: 'Successfully updated',
              errorMessage: 'An item with this name already exists',
            },
          }),
        );
        await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
        await dispatch(
          FetchBlockItemDimensions({
            blockId,
          }),
        );
        await dispatch(handleFilters());

        // Set success state
        setRenameSuccess((previous) => ({ ...previous, [item_id]: true }));
        setIsItemRenamed(true);
        setItemNameField('');

        // Clear success state after a short delay
        setTimeout(() => {
          setRenameSuccess((previous) => ({ ...previous, [item_id]: false }));
        }, 2000);
      } catch {
        // Error is handled by the toaster in the action
        // Silently handle error as it's already shown via toaster
      } finally {
        setIsRenaming((previous) => ({ ...previous, [item_id]: false }));
        isApiCallInProgressReference.current[item_id] = false;
      }
    },
    [dispatch, baseScenario, currentDriver, blockId],
  );

  // Debounced version of UpdateItemApi
  const debouncedUpdateItemApi = useCallback(
    debounce((item_id: number, name: string) => {
      UpdateItemApi(item_id, name);
    }, 500),
    [UpdateItemApi],
  );

  const onKeyPressAction = async (event: any, item_id: number) => {
    if (event.code === 'Enter') {
      // Clear any pending debounced calls
      if (debounceTimeoutReference.current) {
        clearTimeout(debounceTimeoutReference.current);
        debounceTimeoutReference.current = null;
      }
      // Cancel debounce and call immediately
      debouncedUpdateItemApi.cancel();
      const name = event.target.value || itemNameField;
      if (name && name.trim().length > 0) {
        await UpdateItemApi(item_id, name);
      }
    }
    if (event.code === 'Escape') {
      // Clear any pending debounced calls
      if (debounceTimeoutReference.current) {
        clearTimeout(debounceTimeoutReference.current);
        debounceTimeoutReference.current = null;
      }
      debouncedUpdateItemApi.cancel();
      setItemNameField('');
      setIsRenaming((previous) => ({ ...previous, [item_id]: false }));
    }
  };

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutReference.current) {
        clearTimeout(debounceTimeoutReference.current);
      }
      debouncedUpdateItemApi.cancel();
    };
  }, [debouncedUpdateItemApi]);

  const getDisplayProperties = (originalProperties: any) => {
    const properties = JSON.parse(JSON.stringify(originalProperties));
    const startDateKey = Object.keys(properties).find((key) => {
      if (
        `${key}`.toLowerCase().trim() === 'start date' ||
        `${key}`.toLowerCase().trim() === 'start'
      ) {
        return key;
      }
      return false;
    });

    const endDateKey = Object.keys(properties).find((key) => {
      if (`${key}`.toLowerCase().trim() === 'end date' || `${key}`.toLowerCase().trim() === 'end') {
        return key;
      }
      return false;
    });
    if (startDateKey) {
      delete properties[startDateKey];
    }
    if (endDateKey) {
      delete properties[endDateKey];
    }
    return properties;
  };

  const DeleteDimItemModal = (event: any, name: any, id: any) => {
    setDeleteIdModal(id);
    setDeleteNameModal(name);
    setIsDeleteModal(true);
    event.stopPropagation();
  };

  const onDeleteModel = async () => {
    await dispatch(
      PostDeleteDimensionItem({
        ItemId: DeleteIdModal,
        parameters: { scenario_id: baseScenario?.id },
      }),
    );
    await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
    setIsDeleteModal(false);
    setDeleteIdModal(null);
    setDeleteNameModal('');
    await dispatch(
      FetchBlockItemDimensions({
        blockId,
      }),
    );
    await dispatch(handleFilters());
  };

  return (
    <>
      {dataIndicators[dataKey?.name].items?.map((dataElements: any, index: number) => {
        const draggableId: string = JSON.stringify({
          item_id: dataElements.id,
          property_id: currentDriver?.group_property_id,
          value: dataElements?.name,
        });
        return (
          <React.Fragment key={index}>
            <Draggable key={`${dataElements.id}`} draggableId={draggableId} index={index}>
              {(provided: any) => (
                // @ts-ignore - Complex union type from react-beautiful-dnd
                <Flex
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  key={index}
                  textAlign='left'
                  alignSelf='left'
                  w='100%'
                  minH='50px'
                  bgColor='#7163D0'
                  mb='10px'
                  p='0'
                  flexDirection='column'
                  borderRadius='14px'
                  filter='drop-shadow(0px 0px 15.998px rgba(0, 0, 0, 0.25))'
                  transition='all 0.3s ease-out'
                  zIndex={show === dataElements?.id || displayInfo === dataElements?.id ? '2' : '0'}
                  css={styles.container(
                    show === dataElements?.id || displayInfo === dataElements?.id,
                  )}
                >
                  <Box
                    onClick={(event) => {
                      showOrRename(event, dataElements?.id);
                      dispatch(setSelectedElement(_index));
                      dispatch(setDisplayInfo(null));
                    }}
                    position='relative'
                    p='16px'
                    onBlur={() => {
                      if (
                        itemNameField &&
                        itemNameField.trim() &&
                        renameItemName === dataElements?.id
                      ) {
                        debouncedUpdateItemApi(dataElements?.id, itemNameField);
                      }
                    }}
                  >
                    <Flex
                      justifyContent='space-between'
                      className='infoButtonParent'
                      {...longClickCheck()}
                    >
                      {renameItemName === dataElements?.id && !planPermission ? (
                        <Flex alignItems='center' gap='8px' width='100%'>
                          <InputField
                            width={'fit-content'}
                            outline={'none !important'}
                            boxShadow={'none !important'}
                            border={'1px white solid !important'}
                            padding={'15px 5px 15px 10px'}
                            height={'28px'}
                            fontSize={'16px !important'}
                            fontFamily={'Roboto Bold'}
                            defaultValue={dataElements?.name}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const { value } = event.target;
                              setItemNameField(value);
                              // Debounce the API call on change (optional - can be removed if only Enter/blur should trigger)
                              // debouncedUpdateItemApi.cancel();
                              // if (value && value.trim() && value.trim() !== dataElements?.name) {
                              //   debouncedUpdateItemApi(dataElements?.id, value);
                              // }
                            }}
                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                              const target = event.target as HTMLInputElement;
                              if (target.value.trim().length > 0) {
                                onKeyPressAction(event, dataElements?.id);
                              }
                            }}
                            _placeholder={{ color: '#ffffff66' }}
                            autoFocus
                            isDisabled={isRenaming[dataElements?.id]}
                          />
                          {isRenaming[dataElements?.id] && (
                            <Spinner
                              size='sm'
                              thickness='2px'
                              styleProperties={{
                                color: 'white',
                              }}
                            />
                          )}
                          {renameSuccess[dataElements?.id] && !isRenaming[dataElements?.id] && (
                            <Text color='green.300' fontSize='xs'>
                              ✓
                            </Text>
                          )}
                        </Flex>
                      ) : (
                        <Tooltip label={dataElements?.name}>
                          <Text
                            fontFamily='Roboto Bold'
                            fontSize='sx'
                            lineHeight='9'
                            fontWeight='bold'
                            padding={'2px'}
                            noOfLines={isLongClicked ? 0 : 1}
                          >
                            {dataElements?.name}
                          </Text>
                        </Tooltip>
                      )}
                      <Flex alignItems='center' position='relative' ref={reference}>
                        <Menu isOpen={displayInfo === dataElements?.id}>
                          <MenuButton
                            width={'auto'}
                            marginRight='1rem'
                            cursor={planPermission ? 'not-allowed' : 'pointer'}
                            onClick={
                              planPermission
                                ? undefined
                                : (event) => {
                                    event.stopPropagation();
                                    toggleInfoPopup(dataElements?.id);
                                  }
                            }
                          >
                            <Image src={infoIcon} w='28px' height='18px' className='infoButton' />
                          </MenuButton>
                          <MenuList>
                            <MenuItem
                              onClick={(event) => renameItem(event, dataElements?.id)}
                              padding='.5rem 1.5rem'
                            >
                              Rename
                            </MenuItem>
                            <MenuItem
                              onClick={(event) => {
                                DeleteDimItemModal(event, dataElements?.name, dataElements?.id);
                              }}
                              padding='.5rem 1.5rem'
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                        <Image src={show === dataElements?.id ? arrowUp : arrowDown} w='12px' />
                      </Flex>
                    </Flex>
                  </Box>
                  <Collapse isOpened={show === dataElements?.id} width='100%'>
                    <Box mb='15px' p='0 16px 16px 16px'>
                      <DatePickerDrivers
                        values={dataElements?.properties}
                        onSubmitValue={onSubmitValue}
                        id={dataElements?.id}
                        timescale={dataElements?.timescale}
                      />
                      {Object.entries(getDisplayProperties(dataElements?.properties)).map(
                        ([keys, value]) => {
                          return (
                            <React.Fragment key={`${dataElements?.id}-${keys}`}>
                              <DesktopProperties
                                keys={keys}
                                value={value}
                                onSubmitValue={onSubmitValue}
                                dataElementsId={dataElements?.id}
                                planPermission={planPermission}
                              />
                            </React.Fragment>
                          );
                        },
                      )}
                    </Box>
                  </Collapse>
                </Flex>
              )}
            </Draggable>
          </React.Fragment>
        );
      })}
      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />
    </>
  );
};

export default DesktopElements;
